import { useState, useRef, useEffect, useMemo } from 'react'
import { ChevronDown, Search } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { type AdherentStructure, STRUCTURE_CATEGORY } from '@/constants/structures'
import { useAffiliatedOrganizations } from '@/hooks/useAffiliatedOrganizations'
import { useDebouncedValue } from '@/hooks/useDebouncedValue'

interface AdherentStructureComboboxProps {
  selectedStructure: AdherentStructure | null
  onStructureSelect: (structure: AdherentStructure | null) => void
}

export function AdherentStructureCombobox({
  selectedStructure,
  onStructureSelect,
}: AdherentStructureComboboxProps) {
  const { t } = useTranslation('wizard')
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  const debouncedSearch = useDebouncedValue(search, 300)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const [triggerWidth, setTriggerWidth] = useState<number | undefined>(undefined)

  useEffect(() => {
    if (triggerRef.current) {
      setTriggerWidth(triggerRef.current.offsetWidth)
    }
  }, [open])

  const handleOpenChange = (nextOpen: boolean) => {
    setOpen(nextOpen)
    if (!nextOpen) setSearch('')
  }

  const { structures, isLoading } = useAffiliatedOrganizations({
    search: debouncedSearch.trim() || undefined,
    enabled: open,
    limit: 100,
  })

  const { professionalStructures, scientificStructures, trainingStructures } = useMemo(() => {
    const professional = structures.filter(s => s.category === STRUCTURE_CATEGORY.PROFESSIONAL)
    const scientific = structures.filter(s => s.category === STRUCTURE_CATEGORY.SCIENTIFIC)
    const training = structures.filter(s => s.category === STRUCTURE_CATEGORY.TRAINING)
    return {
      professionalStructures: professional,
      scientificStructures: scientific,
      trainingStructures: training,
    }
  }, [structures])

  const handleSelect = (structureId: string) => {
    const structure = structures.find(s => s.id === structureId) || null
    onStructureSelect(structure)
    setOpen(false)
  }

  return (
    <div>
      <label className="mb-2 block text-sm font-semibold text-black">
        {t('rppsVerification.structureLabel')}
        <span className="text-primary">*</span>
      </label>
      <Popover open={open} onOpenChange={handleOpenChange}>
        <PopoverTrigger asChild>
          <button
            ref={triggerRef}
            type="button"
            role="combobox"
            aria-expanded={open}
            className="mb-4 flex w-full items-center justify-between rounded-lg border border-[#858C94] bg-white px-4 py-3.5 text-left text-sm font-normal text-black transition-colors hover:bg-gray-50"
          >
            {selectedStructure
              ? `${selectedStructure.acronym} – ${selectedStructure.fullName}`
              : t('rppsVerification.structurePlaceholder')}
            <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </button>
        </PopoverTrigger>
        <PopoverContent
          className="p-0"
          align="start"
          style={triggerWidth ? { width: `${triggerWidth}px` } : undefined}
        >
          <div className="flex max-h-[300px] flex-col overflow-hidden">
            <div className="flex items-center border-b px-3">
              <Search className="mr-2 h-4 w-4 shrink-0 opacity-50" />
              <input
                type="text"
                placeholder={t('rppsVerification.structureSearchPlaceholder')}
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="flex h-11 w-full rounded-md bg-transparent py-3 text-sm outline-none placeholder:text-sm placeholder:font-normal placeholder:text-[#171721ab] disabled:cursor-not-allowed disabled:opacity-50"
              />
            </div>
            <div className="max-h-[300px] overflow-x-hidden overflow-y-auto">
              {isLoading ? (
                <div className="py-6 text-center text-sm text-gray-500">
                  {t('rppsVerification.structureLoading')}
                </div>
              ) : structures.length === 0 ? (
                <div className="py-6 text-center text-sm text-gray-500">
                  {t('rppsVerification.structureNoData')}
                </div>
              ) : (
                <>
                  {professionalStructures.length > 0 && (
                    <div className="p-1">
                      <div className="mb-2 px-2 py-1.5 text-base font-semibold text-[#474747]">
                        {t('rppsVerification.structures.professional')}
                      </div>
                      {professionalStructures.map(structure => (
                        <button
                          key={structure.id}
                          type="button"
                          onClick={() => handleSelect(structure.id)}
                          className="flex w-full cursor-pointer items-center gap-2 rounded-md p-3 text-sm transition-colors hover:bg-gray-50"
                          style={{ border: '0.5px solid #E1EAEF' }}
                        >
                          <input
                            type="radio"
                            checked={selectedStructure?.id === structure.id}
                            readOnly
                            className="pointer-events-none h-4 w-4 cursor-pointer accent-[#2062F6]"
                          />
                          <span className="flex-1 text-left">
                            <span className="font-medium text-[#B11739]">{structure.acronym}</span>
                            {' – '}
                            <span className="font-normal text-[#3C3C3C]">{structure.fullName}</span>
                          </span>
                        </button>
                      ))}
                    </div>
                  )}
                  {scientificStructures.length > 0 && (
                    <div className="p-1">
                      <div className="mb-2 px-2 py-1.5 text-base font-semibold text-[#474747]">
                        {t('rppsVerification.structures.scientific')}
                      </div>
                      {scientificStructures.map(structure => (
                        <button
                          key={structure.id}
                          type="button"
                          onClick={() => handleSelect(structure.id)}
                          className="flex w-full cursor-pointer items-center gap-2 rounded-md p-3 text-sm transition-colors hover:bg-gray-50"
                          style={{ border: '0.5px solid #E1EAEF' }}
                        >
                          <input
                            type="radio"
                            checked={selectedStructure?.id === structure.id}
                            readOnly
                            className="pointer-events-none h-4 w-4 cursor-pointer accent-[#2062F6]"
                          />
                          <span className="flex-1 text-left">
                            <span className="font-medium text-[#B11739]">{structure.acronym}</span>
                            {' – '}
                            <span className="font-normal text-[#3C3C3C]">{structure.fullName}</span>
                          </span>
                        </button>
                      ))}
                    </div>
                  )}
                  {trainingStructures.length > 0 && (
                    <div className="p-1">
                      <div className="mb-2 px-2 py-1.5 text-base font-semibold text-[#474747]">
                        {t('rppsVerification.structures.training')}
                      </div>
                      {trainingStructures.map(structure => (
                        <button
                          key={structure.id}
                          type="button"
                          onClick={() => handleSelect(structure.id)}
                          className="flex w-full cursor-pointer items-center gap-2 rounded-md p-3 text-sm transition-colors hover:bg-gray-50"
                          style={{ border: '0.5px solid #E1EAEF' }}
                        >
                          <input
                            type="radio"
                            checked={selectedStructure?.id === structure.id}
                            readOnly
                            className="pointer-events-none h-4 w-4 cursor-pointer accent-[#2062F6]"
                          />
                          <span className="flex-1 text-left">
                            <span className="font-medium text-[#B11739]">{structure.acronym}</span>
                            {' – '}
                            <span className="font-normal text-[#3C3C3C]">{structure.fullName}</span>
                          </span>
                        </button>
                      ))}
                    </div>
                  )}
                  {structures.length > 0 &&
                    search &&
                    professionalStructures.length === 0 &&
                    scientificStructures.length === 0 &&
                    trainingStructures.length === 0 && (
                      <div className="py-6 text-center text-sm text-gray-500">
                        {t('rppsVerification.structureEmpty')}
                      </div>
                    )}
                </>
              )}
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  )
}
